import { Warranty, WarrantyClaimStatus } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  useAddWarrantyClaimAttachmentsMutation,
  useDeleteWarrantyClaimAttachmentMutation,
  useUpdateWarrantyClaimFormMutation,
} from '@api/warranty.api';
import {
  BackButton,
  Button,
  WarrantyClaimDetailsSection,
  WarrantyCommentsSection,
  WarrantyCreateForm,
  useWarrantyClaimContext,
} from '@components';
import { useAppSelector, useAuth, useToast } from '@hooks';
import { Box, Stack } from '@mui/material';
import { getSpecialPage, getSystemPages } from '@store/app/app.slice';
import { t } from '@translations';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export const WarrantyDetailsForm = () => {
  const { toggleReadOnly } = useWarrantyClaimContext();
  const [requestUpdate] = useUpdateWarrantyClaimFormMutation();
  const { isBeremaUser, name } = useAuth();
  const [addAttachment] = useAddWarrantyClaimAttachmentsMutation();
  const [removeAttachment] = useDeleteWarrantyClaimAttachmentMutation();

  const dealerWarrantyListPage = useAppSelector(getSpecialPage('dealerWarrantyListPage'));

  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [isSendingToVendor, setIsSendingToVendor] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const disableOrderProcessing = useSelector(getSystemPages).disableOrderProcessing as boolean;

  const { toast } = useToast();
  const { form, currentFileId, handleFileIdUpload } = useWarrantyClaimContext();

  const isRejected = form.watch('status') === WarrantyClaimStatus.Rejected;
  const isAccepted = form.watch('status') === WarrantyClaimStatus.Accepted;
  const isReturned = form.watch('status') === WarrantyClaimStatus.Returned;
  const isSentToVendor = form.watch('status') === WarrantyClaimStatus.SentToVendor;
  const isCredited = form.watch('status') === WarrantyClaimStatus.Credited;
  const isSentToVendorAndCredited =
    form.watch('status') === WarrantyClaimStatus.SentToVendorAndCredited;

  const onSubmit = async (data: Warranty) => {
    setIsSaving(true);

    try {
      if (!isBeremaUser) {
        form.setValue('status', WarrantyClaimStatus.New);
        data.status = WarrantyClaimStatus.New;
      }
      await requestUpdate(data).unwrap();
      if (currentFileId && (!data?.recipeFile || data?.recipeFile?.[0]?.id !== currentFileId)) {
        await removeAttachment({
          warrantyClaimId: data.id,
          attachmentId: currentFileId,
        }).unwrap();
        handleFileIdUpload(null);
      }

      if (data?.recipeFile?.length && data?.recipeFile?.[0]?.id !== currentFileId) {
        const formData = new FormData();
        formData.append('file', data.recipeFile[0]);
        await addAttachment({
          warrantyClaimId: data.id,
          formData: formData,
        })
          .unwrap()
          .then((res) => {
            handleFileIdUpload(res.attachmentId);
          });
      }

      toast({
        severity: 'success',
        message: t(TextCodes.Warranty.Block.WarrantyStatusUpdated),
      });
      form.reset({}, { keepValues: true });
    } catch (error) {
      toast({
        severity: 'error',
        message: t(TextCodes.Warranty.Block.WarrantyStatusUpdateError),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptSubmit = async (formData: Warranty) => {
    setIsAccepting(true);
    try {
      formData.status = WarrantyClaimStatus.Accepted;
      if (name) formData.reviewedBy = name;
      await requestUpdate(formData).unwrap();
      toast({ severity: 'success', message: t(TextCodes.Warranty.Status.Accepted) });
      form.setValue('status', WarrantyClaimStatus.Accepted);
      form.reset({}, { keepValues: true });
    } catch (error) {
      toast({ severity: 'error', message: t(TextCodes.Warranty.Block.AcceptError) });
    } finally {
      setIsAccepting(false);
    }
  };

  const handleRejectSubmit = async (formData: Warranty) => {
    setIsRejecting(true);
    try {
      formData.status = WarrantyClaimStatus.Rejected;
      if (name) formData.reviewedBy = name;
      await requestUpdate(formData).unwrap();
      toast({ severity: 'success', message: t(TextCodes.Warranty.Status.Rejected) });
      form.setValue('status', WarrantyClaimStatus.Rejected);
      form.reset({}, { keepValues: true });
    } catch (error) {
      toast({ severity: 'error', message: 'Reject Error' });
    } finally {
      setIsRejecting(false);
    }
  };

  const handleReturnSubmit = async (formData: Warranty) => {
    setIsReturning(true);
    try {
      formData.status = WarrantyClaimStatus.Returned;
      await requestUpdate(formData).unwrap();
      toast({ severity: 'success', message: t(TextCodes.Warranty.Status.Returned) });
      form.setValue('status', WarrantyClaimStatus.Returned);
      form.reset({}, { keepValues: true });
    } catch (error) {
      toast({ severity: 'error', message: 'Return error' });
    } finally {
      setIsReturning(false);
    }
  };

  const handleSendToVendor = async (formData: Warranty) => {
    setIsSendingToVendor(true);
    try {
      formData.status = WarrantyClaimStatus.SentToVendor;
      await requestUpdate(formData).unwrap();
      toast({ severity: 'success', message: t(TextCodes.Warranty.Status.SentToVendor) });
      form.setValue('status', WarrantyClaimStatus.SentToVendor);
      form.reset({}, { keepValues: true });
    } catch (error) {
      toast({ severity: 'error', message: 'Return error' });
    } finally {
      setIsSendingToVendor(false);
    }
  };

  const handleAccept = () => {
    form.handleSubmit(handleAcceptSubmit)();
  };

  const handleSave = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleReject = () => {
    form.handleSubmit(handleRejectSubmit)();
  };

  const handleReturn = () => {
    form.handleSubmit(handleReturnSubmit)();
  };

  const handleSentToVendor = () => {
    form.handleSubmit(handleSendToVendor)();
  };

  useEffect(() => {
    toggleReadOnly(!isBeremaUser && !isReturned);
  }, [isBeremaUser, isReturned, toggleReadOnly]);

  return (
    <Box component='form' onSubmit={form.handleSubmit(onSubmit)}>
      <WarrantyCreateForm />
      <WarrantyCommentsSection />
      <WarrantyClaimDetailsSection />
      <Box display='flex' justifyContent='flex-end' mt={8}>
        <Stack direction='row' gap={2} flexWrap='wrap'>
          {!isBeremaUser && !isReturned && (
            <BackButton href={dealerWarrantyListPage}>
              {t(TextCodes.Warranty.Block.BackToList)}
            </BackButton>
          )}
          {(isBeremaUser || isReturned) && (
            <Button
              variant='primary'
              size='small'
              disabled={!form.formState.isDirty || isSaving}
              busy={isSaving}
              onClick={handleSave}
            >
              {t(TextCodes.Warranty.Block.Save)}
            </Button>
          )}
          {isBeremaUser && (
            <Button
              variant='primary'
              size='small'
              onClick={() => handleAccept()}
              disabled={
                isAccepted ||
                isAccepting ||
                isCredited ||
                isSentToVendor ||
                isSentToVendorAndCredited ||
                disableOrderProcessing
              }
              busy={isAccepting}
            >
              {t(TextCodes.Warranty.Block.Accept)}
            </Button>
          )}
          {isBeremaUser && (
            <Button
              variant='primary'
              size='small'
              onClick={() => handleReject()}
              disabled={
                isRejected ||
                isRejecting ||
                isAccepted ||
                isCredited ||
                isSentToVendor ||
                isSentToVendorAndCredited
              }
              busy={isRejecting}
            >
              {t(TextCodes.Warranty.Block.Reject)}
            </Button>
          )}
          {isBeremaUser && (
            <Button
              variant='primary'
              size='small'
              onClick={() => handleReturn()}
              disabled={
                isReturned ||
                isReturning ||
                isAccepted ||
                isCredited ||
                isSentToVendor ||
                isSentToVendorAndCredited
              }
              busy={isReturning}
            >
              {t(TextCodes.Warranty.Block.ReturnToDealer)}
            </Button>
          )}
          {isBeremaUser && (
            <Button
              variant='primary'
              size='small'
              onClick={() => handleSentToVendor()}
              disabled={
                (!isAccepted && !isCredited) ||
                isSendingToVendor ||
                isSentToVendor ||
                isSentToVendorAndCredited
              }
              busy={isSendingToVendor}
            >
              {t(TextCodes.Warranty.Block.SendToVendor)}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
